/**
 * FERP Portal - Entity Type Manager Hook
 *
 * Manages entity types for a subgraph: categories, presets, tier-based
 * enable/disable, and dynamic metadata columns.
 *
 * Solves the "IAM anti-pattern" — instead of managing 823 entity types
 * individually, users pick a preset ("Bookkeeper Essentials", "Full ERP")
 * and optionally fine-tune.
 *
 * 3-Level Engagement:
 * - Zero-config: Default preset auto-applied when subgraph enabled
 * - Light-config: Pick from 3-5 presets (30-second decision)
 * - Fine-tuning: Toggle individual entity types in Tabulator table
 *
 * FERP Principles:
 * - Data-driven: Categories, presets, tiers from backend metadata
 * - Dumb frontend: Just displays what backend defines
 * - Progressive disclosure: Reduce cognitive load for 100+ entity types
 */

import { useMemo, useCallback, useState } from 'react';
import type {
  SubgraphEntityType,
  EntityCategory,
  EntityPreset,
  CategoryRule,
  EntityTier,
  SubgraphInfo,
} from '@/hooks/useTenantSubgraphConfig';

// ============================================================================
// Types
// ============================================================================

/** Entity type enriched with resolved category and tier */
export interface ManagedEntityType extends SubgraphEntityType {
  /** Resolved category (from explicit field or category_rules) */
  resolvedCategory: string;
  /** Resolved tier (from explicit field or category_rules) */
  resolvedTier: EntityTier;
  /** Category display name for Tabulator grouping */
  categoryDisplayName: string;
  /** Whether this entity type is currently enabled */
  isEnabled: boolean;
  /** Whether this entity type's toggle is locked (essential tier) */
  isLocked: boolean;
}

/** A category with its child entity types */
export interface CategoryGroup {
  category: EntityCategory;
  entityTypes: ManagedEntityType[];
  enabledCount: number;
  totalCount: number;
}

/** Dynamic metadata column derived from entity metadata keys */
export interface MetadataColumn {
  key: string;
  displayName: string;
  /** Count of entities that have this metadata key */
  coverage: number;
}

export interface UseEntityTypeManagerResult {
  /** All entity types with resolved category/tier */
  managedTypes: ManagedEntityType[];
  /** Entity types grouped by category */
  categoryGroups: CategoryGroup[];
  /** Available categories for this subgraph */
  categories: EntityCategory[];
  /** Available presets for this subgraph */
  presets: EntityPreset[];
  /** Currently active preset (null if custom selection) */
  activePreset: EntityPreset | null;
  /** Dynamic metadata columns discovered from entity metadata */
  metadataColumns: MetadataColumn[];
  /** Summary stats */
  stats: {
    totalTypes: number;
    enabledTypes: number;
    essentialCount: number;
    standardCount: number;
    advancedCount: number;
    specialistCount: number;
    categoryCount: number;
  };
  /** Apply a preset (enables its entity codes, disables others) */
  applyPreset: (presetId: string) => void;
  /** Toggle a single entity type */
  toggleEntityType: (entityName: string, enabled: boolean) => void;
  /** Bulk toggle all entity types in a category */
  toggleCategory: (categoryId: string, enabled: boolean) => void;
  /** Reset to default preset */
  resetToDefault: () => void;
  /** Check if current selection matches a preset */
  matchesPreset: (presetId: string) => boolean;
}

// ============================================================================
// Tier priority (for sorting and default enable state)
// ============================================================================

const TIER_ORDER: Record<EntityTier, number> = {
  essential: 0,
  standard: 1,
  advanced: 2,
  specialist: 3,
};

const TIER_DISPLAY: Record<EntityTier, string> = {
  essential: 'Essential',
  standard: 'Standard',
  advanced: 'Advanced',
  specialist: 'Specialist',
};

/** Tiers that are enabled by default */
const DEFAULT_ENABLED_TIERS: Set<EntityTier> = new Set(['essential', 'standard']);

// ============================================================================
// Helper: resolve category and tier from rules
// ============================================================================

function resolveEntityCategoryTier(
  entity: SubgraphEntityType,
  rules: CategoryRule[],
): { category: string; tier: EntityTier } {
  // Explicit fields take precedence
  if (entity.category && entity.tier) {
    return {
      category: entity.category,
      tier: entity.tier as EntityTier,
    };
  }

  // Apply category rules (first match wins)
  for (const rule of rules) {
    if (entity.name.startsWith(rule.patternPrefix)) {
      return {
        category: entity.category ?? rule.category,
        tier: (entity.tier as EntityTier) ?? (rule.tier as EntityTier),
      };
    }
  }

  // Fallback
  return {
    category: entity.category ?? 'uncategorized',
    tier: (entity.tier as EntityTier) ?? 'specialist',
  };
}

/** Convert metadata key to display name (e.g., "ofbiz_module" → "OFBiz Module") */
function metadataKeyToDisplayName(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ============================================================================
// Hook
// ============================================================================

export function useEntityTypeManager(
  subgraphInfo: SubgraphInfo | null,
  entityTypes: SubgraphEntityType[],
): UseEntityTypeManagerResult {
  const categories = useMemo(
    () => subgraphInfo?.entityCategories ?? [],
    [subgraphInfo],
  );
  const categoryRules = useMemo(
    () => subgraphInfo?.categoryRules ?? [],
    [subgraphInfo],
  );
  const presets = useMemo(
    () => subgraphInfo?.entityPresets ?? [],
    [subgraphInfo],
  );

  // Category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, EntityCategory>();
    for (const cat of categories) {
      map.set(cat.id, cat);
    }
    // Add fallback for uncategorized
    if (!map.has('uncategorized')) {
      map.set('uncategorized', {
        id: 'uncategorized',
        displayName: 'Uncategorized',
        description: 'Entity types not assigned to a category',
        icon: 'IconQuestionMark',
        sortOrder: 99999,
      });
    }
    return map;
  }, [categories]);

  // Find default preset
  const defaultPreset = useMemo(
    () => presets.find((p) => p.isDefault) ?? null,
    [presets],
  );

  // Track enabled entity codes (state)
  const [enabledCodes, setEnabledCodes] = useState<Set<string>>(() => {
    // Initialize from default preset or tier-based defaults
    if (defaultPreset) {
      if (defaultPreset.entityCodes.length === 0) {
        // ALL preset
        return new Set(entityTypes.map((et) => et.name));
      }
      return new Set(defaultPreset.entityCodes);
    }
    // No default preset: enable essential + standard tiers
    const enabled = new Set<string>();
    for (const et of entityTypes) {
      const { tier } = resolveEntityCategoryTier(et, categoryRules);
      if (DEFAULT_ENABLED_TIERS.has(tier)) {
        enabled.add(et.name);
      }
    }
    return enabled;
  });

  // Build managed entity types with resolved category/tier
  const managedTypes = useMemo(() => {
    return entityTypes.map((et): ManagedEntityType => {
      const { category, tier } = resolveEntityCategoryTier(et, categoryRules);
      const catInfo = categoryMap.get(category);
      return {
        ...et,
        resolvedCategory: category,
        resolvedTier: tier,
        categoryDisplayName: catInfo?.displayName ?? category,
        isEnabled: enabledCodes.has(et.name),
        isLocked: tier === 'essential', // Essential entities can't be disabled
      };
    });
  }, [entityTypes, categoryRules, categoryMap, enabledCodes]);

  // Group by category
  const categoryGroups = useMemo(() => {
    const groups = new Map<string, ManagedEntityType[]>();
    for (const mt of managedTypes) {
      const existing = groups.get(mt.resolvedCategory) ?? [];
      existing.push(mt);
      groups.set(mt.resolvedCategory, existing);
    }

    const result: CategoryGroup[] = [];
    for (const [catId, types] of groups) {
      const catInfo = categoryMap.get(catId) ?? {
        id: catId,
        displayName: catId,
        description: '',
        icon: 'IconFolder',
        sortOrder: 99999,
      };
      result.push({
        category: catInfo,
        entityTypes: types.sort((a, b) => {
          // Sort by tier (essential first), then alphabetically
          const tierDiff = TIER_ORDER[a.resolvedTier] - TIER_ORDER[b.resolvedTier];
          if (tierDiff !== 0) return tierDiff;
          return a.name.localeCompare(b.name);
        }),
        enabledCount: types.filter((t) => t.isEnabled).length,
        totalCount: types.length,
      });
    }

    return result.sort((a, b) => a.category.sortOrder - b.category.sortOrder);
  }, [managedTypes, categoryMap]);

  // Discover dynamic metadata columns
  const metadataColumns = useMemo(() => {
    const keyCounts = new Map<string, number>();
    for (const et of entityTypes) {
      if (et.metadata) {
        for (const key of Object.keys(et.metadata)) {
          keyCounts.set(key, (keyCounts.get(key) ?? 0) + 1);
        }
      }
    }

    const columns: MetadataColumn[] = [];
    for (const [key, count] of keyCounts) {
      columns.push({
        key,
        displayName: metadataKeyToDisplayName(key),
        coverage: count,
      });
    }

    return columns.sort((a, b) => b.coverage - a.coverage);
  }, [entityTypes]);

  // Stats
  const stats = useMemo(() => {
    const tierCounts: Record<EntityTier, number> = {
      essential: 0,
      standard: 0,
      advanced: 0,
      specialist: 0,
    };
    for (const mt of managedTypes) {
      tierCounts[mt.resolvedTier]++;
    }
    return {
      totalTypes: managedTypes.length,
      enabledTypes: managedTypes.filter((mt) => mt.isEnabled).length,
      essentialCount: tierCounts.essential,
      standardCount: tierCounts.standard,
      advancedCount: tierCounts.advanced,
      specialistCount: tierCounts.specialist,
      categoryCount: categoryGroups.length,
    };
  }, [managedTypes, categoryGroups]);

  // Find active preset (matches current enabled set)
  const activePreset = useMemo(() => {
    for (const preset of presets) {
      if (preset.entityCodes.length === 0) {
        // ALL preset — matches if everything is enabled
        if (enabledCodes.size === entityTypes.length) return preset;
        continue;
      }
      const presetSet = new Set(preset.entityCodes);
      // Must also include all essential types
      const essentialTypes = managedTypes
        .filter((mt) => mt.resolvedTier === 'essential')
        .map((mt) => mt.name);
      const expectedSet = new Set([...presetSet, ...essentialTypes]);
      if (
        enabledCodes.size === expectedSet.size &&
        [...enabledCodes].every((code) => expectedSet.has(code))
      ) {
        return preset;
      }
    }
    return null;
  }, [presets, enabledCodes, entityTypes, managedTypes]);

  // Actions
  const applyPreset = useCallback(
    (presetId: string) => {
      const preset = presets.find((p) => p.id === presetId);
      if (!preset) return;

      if (preset.entityCodes.length === 0) {
        // ALL preset
        setEnabledCodes(new Set(entityTypes.map((et) => et.name)));
        return;
      }

      // Include preset codes + all essential types
      const essentialTypes = entityTypes
        .filter((et) => {
          const { tier } = resolveEntityCategoryTier(et, categoryRules);
          return tier === 'essential';
        })
        .map((et) => et.name);

      setEnabledCodes(new Set([...preset.entityCodes, ...essentialTypes]));
    },
    [presets, entityTypes, categoryRules],
  );

  const toggleEntityType = useCallback(
    (entityName: string, enabled: boolean) => {
      // Don't allow disabling essential types
      const entity = managedTypes.find((mt) => mt.name === entityName);
      if (entity?.isLocked && !enabled) return;

      setEnabledCodes((prev) => {
        const next = new Set(prev);
        if (enabled) {
          next.add(entityName);
        } else {
          next.delete(entityName);
        }
        return next;
      });
    },
    [managedTypes],
  );

  const toggleCategory = useCallback(
    (categoryId: string, enabled: boolean) => {
      const group = categoryGroups.find((g) => g.category.id === categoryId);
      if (!group) return;

      setEnabledCodes((prev) => {
        const next = new Set(prev);
        for (const et of group.entityTypes) {
          if (et.isLocked && !enabled) continue; // Don't disable essential
          if (enabled) {
            next.add(et.name);
          } else {
            next.delete(et.name);
          }
        }
        return next;
      });
    },
    [categoryGroups],
  );

  const resetToDefault = useCallback(() => {
    if (defaultPreset) {
      applyPreset(defaultPreset.id);
    } else {
      // Reset to tier-based defaults
      const enabled = new Set<string>();
      for (const et of entityTypes) {
        const { tier } = resolveEntityCategoryTier(et, categoryRules);
        if (DEFAULT_ENABLED_TIERS.has(tier)) {
          enabled.add(et.name);
        }
      }
      setEnabledCodes(enabled);
    }
  }, [defaultPreset, applyPreset, entityTypes, categoryRules]);

  const matchesPreset = useCallback(
    (presetId: string) => {
      return activePreset?.id === presetId;
    },
    [activePreset],
  );

  return {
    managedTypes,
    categoryGroups,
    categories,
    presets,
    activePreset,
    metadataColumns,
    stats,
    applyPreset,
    toggleEntityType,
    toggleCategory,
    resetToDefault,
    matchesPreset,
  };
}

export { TIER_ORDER, TIER_DISPLAY };
export default useEntityTypeManager;
